/**
 * A commission plan's expanded row (TMMPDM-0041): its phases (From month, To month or "Onward",
 * Rate), its dates and its currency. Each value is the server's own field, formatted.
 */
import { useEffect, useId, useRef } from 'react';
import {
  Box,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import type { CommissionPlanView, ResellerAffiliateRow } from '@/api/tmm/types';
import { formatDate, formatPercentBp } from '@/format';
import { Amount } from '@/components/ui';
import { ErrorState } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AffiliateStatusChip } from '../affiliates/affiliateShared';

export function CommissionPlanDetail({
  plan,
  resellerId,
  affiliates,
  affiliatesFailure,
  focusAssigned,
  focusToken,
  onRetryAffiliates,
}: {
  plan: CommissionPlanView;
  resellerId: number;
  /** The reseller's affiliates, loaded once for the tab and shared by every row. */
  affiliates: ResellerAffiliateRow[] | null;
  affiliatesFailure: unknown;
  focusAssigned: boolean;
  focusToken: number;
  onRetryAffiliates: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const tableLabel = t('commissionPlans.detail.phasesAria', 'Phases of {{name}}', {
    name: plan.name,
  });
  const facts: { key: string; label: string; value: string }[] = [
    {
      key: 'currency',
      label: t('commissionPlans.detail.currency', 'Currency'),
      value: plan.currency,
    },
    {
      key: 'created',
      label: t('commissionPlans.detail.created', 'Created'),
      value: formatDate(plan.createdAt, locale),
    },
  ];
  if (plan.activatedAt !== undefined) {
    facts.push({
      key: 'activated',
      label: t('commissionPlans.detail.activated', 'Activated'),
      value: formatDate(plan.activatedAt, locale),
    });
  }
  if (plan.closedAt !== undefined) {
    facts.push({
      key: 'closed',
      label: t('commissionPlans.detail.closed', 'Closed'),
      value: formatDate(plan.closedAt, locale),
    });
  }
  if (plan.noncompliantSince !== undefined) {
    facts.push({
      key: 'noncompliant',
      label: t('commissionPlans.detail.noncompliantSince', 'Over a limit since'),
      value: formatDate(plan.noncompliantSince, locale),
    });
  }
  const milestones = plan.milestones ?? [];
  const none = t('commissionPlans.detail.none', 'None');
  const noCap = t('commissionPlans.detail.noCap', 'No cap');

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ minWidth: 0, maxWidth: 420, flex: 1 }}>
          <Table size="small" aria-label={tableLabel}>
            <TableHead>
              <TableRow>
                <TableCell>{t('commissionPlans.detail.fromMonth', 'From month')}</TableCell>
                <TableCell>{t('commissionPlans.detail.toMonth', 'To month')}</TableCell>
                <TableCell align="right">{t('commissionPlans.detail.rate', 'Rate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.phases.map((phase) => (
                <TableRow key={phase.fromMonth}>
                  <TableCell>{phase.fromMonth}</TableCell>
                  <TableCell>
                    {phase.toMonth ?? t('commissionPlans.detail.onward', 'Onward')}
                  </TableCell>
                  <TableCell align="right">{formatPercentBp(phase.rateBp, locale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Box component="dl" sx={{ m: 0 }}>
            {facts.map((fact) => (
              <Box key={fact.key} sx={{ display: 'flex', gap: 1 }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {fact.label}:
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {fact.value}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {t('commissionPlans.detail.bounty', 'Bounty')}:
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {plan.bountyMinor != null && plan.bountyOnPayment != null ? (
                  <>
                    <Amount minor={plan.bountyMinor} currency={plan.currency} locale={locale} />{' '}
                    {t('commissionPlans.detail.onPayment', 'on payment {{n}}', {
                      n: plan.bountyOnPayment,
                    })}
                  </>
                ) : (
                  none
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {t('commissionPlans.detail.perReferralCap', 'Per referral')}:
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {plan.perReferralCapMinor != null ? (
                  <Amount
                    minor={plan.perReferralCapMinor}
                    currency={plan.currency}
                    locale={locale}
                  />
                ) : (
                  noCap
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {t('commissionPlans.detail.perAffiliateMonthCap', 'Per affiliate per month')}:
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {plan.perAffiliateMonthCapMinor != null ? (
                  <Amount
                    minor={plan.perAffiliateMonthCapMinor}
                    currency={plan.currency}
                    locale={locale}
                  />
                ) : (
                  noCap
                )}
              </Typography>
            </Box>
          </Box>
          {milestones.length > 0 ? (
            <Table
              size="small"
              aria-label={t('commissionPlans.detail.milestonesAria', 'Milestones of {{name}}', {
                name: plan.name,
              })}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('commissionPlans.detail.qualifiedReferrals', 'Qualified referrals')}
                  </TableCell>
                  <TableCell align="right">{t('commissionPlans.detail.bonus', 'Bonus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {milestones.map((row) => (
                  <TableRow key={row.qualifiedReferrals}>
                    <TableCell>{row.qualifiedReferrals}</TableCell>
                    <TableCell align="right">
                      <Amount minor={row.amountMinor} currency={plan.currency} locale={locale} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2">
              {t('commissionPlans.detail.milestones', 'Milestones')}: {none}
            </Typography>
          )}
        </Stack>
      </Stack>
      <AssignedTo
        plan={plan}
        resellerId={resellerId}
        affiliates={affiliates}
        failure={affiliatesFailure}
        focusAssigned={focusAssigned}
        focusToken={focusToken}
        onRetry={onRetryAffiliates}
      />
    </Stack>
  );
}

/** Affiliates whose agreement names this plan. Filtered on `commissionPlan.id` only. */
function AssignedTo({
  plan,
  resellerId,
  affiliates,
  failure,
  focusAssigned,
  focusToken,
  onRetry,
}: {
  plan: CommissionPlanView;
  resellerId: number;
  affiliates: ResellerAffiliateRow[] | null;
  failure: unknown;
  focusAssigned: boolean;
  focusToken: number;
  onRetry: () => void;
}) {
  const { t } = useCopy();
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!focusAssigned) return;
    heading.current?.focus();
    heading.current?.scrollIntoView?.({ block: 'center' });
  }, [focusAssigned, focusToken]);
  const assigned =
    affiliates === null
      ? []
      : affiliates.filter(
          (row) => row.commissionPlan !== null && row.commissionPlan.id === plan.id,
        );
  return (
    <Box component="section" aria-labelledby={headingId}>
      <Typography
        ref={heading}
        tabIndex={-1}
        id={headingId}
        variant="subtitle2"
        component="h3"
        sx={{ mb: 1 }}
      >
        {t('commissionPlans.assigned.title', 'Assigned to')}
      </Typography>
      {failure ? (
        <ErrorState
          error={failure}
          message={t('commissionPlans.assigned.loadError', 'Could not load affiliates.')}
          onRetry={onRetry}
        />
      ) : affiliates === null ? (
        <LoadingSpinner />
      ) : (
        <Table
          size="small"
          aria-label={t('commissionPlans.assigned.tableAria', 'Affiliates assigned to {{name}}', {
            name: plan.name,
          })}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('commissionPlans.assigned.affiliate', 'Affiliate')}</TableCell>
              <TableCell>{t('commissionPlans.assigned.tenant', 'Tenant')}</TableCell>
              <TableCell>{t('commissionPlans.assigned.status', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assigned.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  {t('commissionPlans.assigned.empty', 'No affiliates are on this plan.')}
                </TableCell>
              </TableRow>
            ) : (
              assigned.map((row) => (
                <TableRow key={`${row.tenantSlug}/${row.id}`}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/partners/${resellerId}/affiliates/${row.id}`}
                      underline="hover"
                    >
                      {row.displayName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link component={RouterLink} to={`/partners/${resellerId}`} underline="hover">
                      {row.tenantSlug}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <AffiliateStatusChip status={row.status} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
